import { advanceToCell, getActivePlayerByGameObject } from "./bonusCardHelpers"
import {
  getAnimationTimings,
  getHouseTaxPerHouse,
  getPropertyTaxPerProperty,
} from "./constants"
import { mapDesigns } from "./mapDesigns"
import { calculateTotalDurationOfHouseChangeAnimation } from "./propmngHelpers"
const getRandomId = () => crypto.randomUUID()
const getMapById = (id: string) => mapDesigns.find((map) => map.id === id)
const getRandomCardFromDeck = (
  game: IGame,
  count: number = 1
): ActionCardId[] | null => {
  // 1. Clone the cardsInDeck
  const deck = { ...game.situation.cardsInDeck }
  const myMap = getMapById(game.settings.mapId)
  const allUnsoldProperties = myMap.cells
    .filter((cell) => cell.type === "property")
    .filter(
      (cell) =>
        !game.situation.players
          .flatMap((player) => player.properties)
          .includes(cell.order)
    )
  if (allUnsoldProperties.length <= 3) {
    deck["forced-auction"] = 0
    deck["forced-buy"] = 0
    deck["purchase-any-property-for-double-price"] = 0
  }

  // 2. Subtract cards held by players
  for (const player of game.situation.players) {
    for (const card of player.cards) {
      if (deck[card.cardId] && deck[card.cardId] > 0) {
        deck[card.cardId]--
      }
    }
  }

  // Helper to build the available cards array
  const buildAvailableCards = (deckObj: typeof deck) => {
    const available: ActionCardId[] = []
    for (const cardId in deckObj) {
      for (let i = 0; i < deckObj[cardId]; i++) {
        available.push(cardId as ActionCardId)
      }
    }
    return available
  }

  if (count === 1) {
    const availableCards = buildAvailableCards(deck)
    if (availableCards.length === 0) return null
    const randomIndex = Math.floor(Math.random() * availableCards.length)
    return [availableCards[randomIndex]]
  } else {
    const drawn: ActionCardId[] = []
    let tempDeck = { ...deck }
    for (let i = 0; i < count; i++) {
      const availableCards = buildAvailableCards(tempDeck)
      if (availableCards.length === 0) break
      const randomIndex = Math.floor(Math.random() * availableCards.length)
      const cardId = availableCards[randomIndex]
      drawn.push(cardId)
      // Subtract this card from tempDeck for next draw
      tempDeck[cardId]--
    }
    return drawn.length ? drawn : null
  }
}

const bankOrder = 100
export const SayNo: BonusCardChangeFunction = (game) => {
  return { game: { ...game }, customEvents: [], postCardDrawEvents: [] }
}

export const performTaxAudit: BonusCardChangeFunction = (
  game,
  _,
  __,
  { targetedPlayerOrder }
) => {
  const targetedPlayer = game.situation.players.find(
    (item) => item.order === targetedPlayerOrder
  )
  const moneyToTake = Math.floor(targetedPlayer.money * 0.2)
  return {
    game: {
      ...game,
      situation: {
        ...game.situation,
        moneyOnTable: game.situation.moneyOnTable + moneyToTake,
        players: game.situation.players.map((p) =>
          p.order === targetedPlayerOrder
            ? { ...p, money: p.money - moneyToTake }
            : p
        ),
      },
    },
    customEvents: [],
    postCardDrawEvents: [
      {
        type: "money-transfer",
        money: moneyToTake,
        from: targetedPlayer.order,
        to: bankOrder,
        reason: "action-card",
      },
    ],
  }
}

export const get200FromAnyPlayer: BonusCardChangeFunction = (
  game,
  _,
  __,
  { targetedPlayerOrder }
) => {
  const targetedPlayer = game.situation.players.find(
    (item) => item.order === targetedPlayerOrder
  )
  const moneyToTake = 100
  const activePlayer = game.situation.players.find(
    (p) => p.id === game.situation.turn
  )
  return {
    game: {
      ...game,
      situation: {
        ...game.situation,
        players: game.situation.players
          .map((p) =>
            p.order === targetedPlayerOrder
              ? { ...p, money: p.money - moneyToTake }
              : p
          )
          .map((p) =>
            p.order === activePlayer.order
              ? { ...p, money: p.money + moneyToTake }
              : p
          ),
      },
    },
    customEvents: [],
    postCardDrawEvents: [
      {
        type: "money-transfer",
        money: moneyToTake,
        from: targetedPlayer.order,
        to: activePlayer.order,
        reason: "action-card",
      },
    ],
  }
}

export const cardsInDeck: { [key in ActionCardId]: number } = {
  "change-role-for-three-turns": 0,
  "get-two-more-cards": 0,
  "advance-to-go": 4,
  "say-no": 0,
  "dobule-rent": 4,
  "no-effect": 0,
  "get-200-from-player": 5,
  "perform-tax-audit": 5,
  "get-10-percent-from-player": 5,
  "get-10-percent-from-all-players": 4,
  "get-interest": 5,
  "forced-auction": 5,
  "forced-buy": 4,
  "force-downgrade-property": 5,
  "safe-round": 0,
  "purchase-any-property-for-double-price": 2,
  "apply-property-tax": 4,
  "sell-back-property-at-high-price": 0,
  "use-stock-exchange": 4,
  "swap-position-with-player": 4,
  "apply-house-tax": 3,
  "rent-increase-of-property": 0,
}
export const changeRoleForThreeTurns: BonusCardChangeFunction = (
  game,
  _,
  __,
  { changeRole }
) => {
  const activePlayer = game.situation.players.find(
    (p) => p.id === game.situation.turn
  )
  return {
    game: {
      ...game,
      situation: {
        ...game.situation,
        players: game.situation.players.map((p) =>
          p.order === activePlayer.order
            ? {
                ...p,
                role: changeRole,
                roleChangeData: {
                  actualRole: p.role,
                  tempRole: changeRole,
                  turnsLeft: 3,
                },
              }
            : p
        ),
      },
    },
    customEvents: [],
    postCardDrawEvents: [],
  }
}

export const get10PercectFromAnyPlayer: BonusCardChangeFunction = (
  game,
  _,
  __,
  { targetedPlayerOrder }
) => {
  const targetedPlayer = game.situation.players.find(
    (item) => item.order === targetedPlayerOrder
  )
  const moneyToTake = Math.floor(targetedPlayer.money * 0.1)
  const activePlayer = game.situation.players.find(
    (p) => p.id === game.situation.turn
  )
  return {
    game: {
      ...game,
      situation: {
        ...game.situation,
        players: game.situation.players
          .map((p) =>
            p.order === targetedPlayerOrder
              ? { ...p, money: p.money - moneyToTake }
              : p
          )
          .map((p) =>
            p.order === activePlayer.order
              ? { ...p, money: p.money + moneyToTake }
              : p
          ),
      },
    },
    customEvents: [],
    postCardDrawEvents: [
      {
        type: "money-transfer",
        money: moneyToTake,
        from: targetedPlayer.order,
        to: activePlayer.order,
        reason: "action-card",
      },
    ],
  }
}

export const get10PercentFromAllPlayers: BonusCardChangeFunction = (
  game,
  _,
  __
) => {
  const activePlayer = game.situation.players.find(
    (p) => p.id === game.situation.turn
  )
  const otherActivePlayers = game.situation.players.filter(
    (p) => p.order !== activePlayer.order && !p.isBankrupt
  )

  const moneyTransferEvents: IMoneyTransferAnimationEvent[] =
    otherActivePlayers.map((p) => ({
      type: "money-transfer",
      money: p.money < 0 ? 0 : Math.floor(p.money * 0.1),
      from: p.order,
      to: activePlayer.order,
      reason: "action-card",
    }))
  const moneyToTake = moneyTransferEvents.reduce(
    (acc, curr) => acc + curr.money,
    0
  )
  const newPlayers = game.situation.players.map((p) =>
    p.order === activePlayer.order
      ? { ...p, money: p.money + moneyToTake }
      : {
          ...p,
          money: p.money < 0 ? p.money : p.money - Math.floor(p.money * 0.1),
        }
  )
  return {
    game: {
      ...game,
      situation: {
        ...game.situation,
        players: newPlayers,
      },
    },
    customEvents: [],
    postCardDrawEvents: [...moneyTransferEvents],
  }
}

export const swapPositionWithAnyPlayer: BonusCardChangeFunction = (
  game,
  _,
  __,
  { targetedPlayerOrder }
) => {
  const targetedPlayer = game.situation.players.find(
    (item) => item.order === targetedPlayerOrder
  )
  const moneyToTake = Math.floor(targetedPlayer.money * 0.1)
  const activePlayer = game.situation.players.find(
    (p) => p.id === game.situation.turn
  )
  return {
    game: {
      ...game,
      situation: {
        ...game.situation,
        players: game.situation.players.map((p) =>
          p.order === targetedPlayerOrder
            ? { ...p, position: activePlayer.position }
            : p.order === activePlayer.order
              ? { ...p, position: targetedPlayer.position }
              : p
        ),
        // players: game.situation.players
        //   .map((p) =>
        //     p.order === targetedPlayerOrder
        //       ? { ...p, money: p.money - moneyToTake }
        //       : p
        //   )
        //   .map((p) =>
        //     p.order === activePlayer.order
        //       ? { ...p, money: p.money + moneyToTake }
        //       : p
        //   ),
      },
    },
    customEvents: [],
    postCardDrawEvents: [
      // {
      //   type: "piece-movement",
      //   from: activePlayer.position,
      //   to: targetedPlayer.position,
      //   playerOrder: activePlayer.order,
      //   movementType: "no-jump",
      // },
      // {
      //   type: "piece-movement",
      //   from: targetedPlayer.position,
      //   to: activePlayer.position,
      //   playerOrder: targetedPlayer.order,
      //   movementType: "no-jump",
      // },
    ],
  }
}

export const allChances: IActionCard[] = [
  // {
  //   id: "no-effect",
  //   changeFunction: (game) => ({
  //     customEvents: [],
  //     game: {
  //       ...game,
  //       situation: { ...game.situation, endTurnEnabled: true },
  //     },
  //     postCardDrawEvents: [],
  //   }),
  //   image: "",
  //   text: "No effect",
  // },
  {
    id: "say-no",
    text: "Say no",
    changeFunction: SayNo,
    image: "",
  },
  {
    id: "change-role-for-three-turns",
    text: "Change role for three turns",
    changeFunction: changeRoleForThreeTurns,
    image: "",
  },
  {
    id: "rent-increase-of-property",
    text: "Rent increase of property",
    changeFunction: (game, _, __, { targetedPropertyOrder }) => {
      // return { game: game, customEvents: [], postCardDrawEvents: [] }
      return {
        game: {
          ...game,
          situation: {
            ...game.situation,
            doubleRentProperties: [
              ...game.situation.doubleRentProperties,
              { propertyOrder: targetedPropertyOrder, turnsLeft: 3 },
            ],
          },
        },
        customEvents: [],
        postCardDrawEvents: [],
      }
    },
    image: "",
  },
  {
    id: "apply-house-tax",
    text: "Apply house tax on any player",
    changeFunction: (game, _, __, { targetedPlayerOrder }) => {
      // console.log(targetedPropertyOrder, "Property order forced to downgrade")
      const targetedPlayer = game.situation.players.find(
        (item) => item.order === targetedPlayerOrder
      )
      // const totalHouseCount = getTotalHouseCountOfPlayer(game, targetedPlayer)
      const moneyToTake = getHouseTaxPerHouse(game.settings.mapId)
      const taxToPay = game.situation.housesData
        .filter((house) =>
          targetedPlayer.properties.includes(house.propertyOrder)
        )
        .reduce((acc, curr) => acc + curr.houses * moneyToTake, 0)
      return {
        game: {
          ...game,
          situation: {
            ...game.situation,
            moneyOnTable: game.situation.moneyOnTable + taxToPay,
            players: game.situation.players.map((player) =>
              player.order === targetedPlayerOrder
                ? { ...player, money: player.money - taxToPay }
                : player
            ),
          },
        },
        customEvents: [],
        postCardDrawEvents: [
          {
            type: "money-transfer",
            from: targetedPlayerOrder,
            to: bankOrder,
            money: taxToPay,
            reason: "action-card",
          },
        ],
      }
    },
    image: "",
  },
  {
    id: "swap-position-with-player",
    text: "Swap position with any player",
    changeFunction: swapPositionWithAnyPlayer,
    image: "",
  },

  {
    id: "get-two-more-cards",
    image: "",
    text: "Get two more cards",
    changeFunction: (game) => {
      const map = mapDesigns.find((map) => map.id === game.settings.mapId)
      const activePlayer = game.situation.players.find(
        (player) => player.id === game.situation.turn
      )
      const [randomChance, randomChance2] = getRandomCardFromDeck(game, 2)

      //
      // const {
      //   customEvents: customEventsFromChanceFunciton,
      //   game: gameFromChanceFunction,
      //   postCardDrawEvents,
      // } = randomChance.changeFunction(game)

      const id = getRandomId()
      const id2 = getRandomId()
      const keepCardEvent: IKeepActionCardEvent = {
        cardId: randomChance,
        type: "keep-action-card",
        cardType: "chance",
        id,
        playerOrder: activePlayer.order,
      }
      const keepCardEvent2: IKeepActionCardEvent = {
        cardId: randomChance2,
        type: "keep-action-card",
        cardType: "chance",
        id: id2,
        playerOrder: activePlayer.order,
        delay: getAnimationTimings(game).drawAndKeepCard,
      }
      const card1: IPlayerActionCard = {
        cardId: randomChance,
        id,
        playable: true,
      }
      const card2: IPlayerActionCard = {
        cardId: randomChance2,
        id: id2,
        playable: true,
      }
      return {
        customEvents: [],
        // postDrawCardEvents: postCardDrawEvents,
        postCardDrawEvents: [keepCardEvent, keepCardEvent2],
        game: {
          ...game,
          situation: {
            ...game.situation,
            ...game.situation,
            players: game.situation.players.map((player) =>
              player.id === activePlayer.id
                ? {
                    ...player,
                    cards: [...activePlayer.cards, card1, card2],
                  }
                : player
            ),

            recentlyDrawnChanceId: randomChance as ChanceId,
            recentlyDrawnCommunityChestId: "",
          },
        },
      }
    },
  },

  {
    id: "advance-to-go",
    text: "Advance to Start",
    changeFunction: advanceToCell(0),
    image: "",
  },
  {
    id: "use-stock-exchange",
    text: "Use stock exchange",
    changeFunction: (game) => {
      return {
        game: game,
        customEvents: [],
        postCardDrawEvents: [{ type: "show-stock-exchange" }],
      }
    },
    image: "",
  },

  {
    id: "dobule-rent",
    text: "Double the rent",
    changeFunction: (game) => ({
      game,
      customEvents: [],
      postCardDrawEvents: [],
    }),
    image: "",
  },
  {
    id: "get-200-from-player",
    text: "Get 100 from any player",
    changeFunction: get200FromAnyPlayer,
    image: "",
  },
  {
    id: "perform-tax-audit",
    text: "Make any player pay 20% tax to the bank",
    changeFunction: performTaxAudit,
    image: "perform-tax-audit",
  },
  {
    id: "get-10-percent-from-player",
    text: "Get 10% from any player",
    changeFunction: get10PercectFromAnyPlayer,
    image: "",
  },
  {
    id: "get-10-percent-from-all-players",
    text: "Get 10% from all players",
    changeFunction: get10PercentFromAllPlayers,
    image: "get-10-percent-from-all-players",
  },
  {
    id: "get-interest",
    text: "Get 20% interest on your total money",
    changeFunction: (game, _, __) => {
      const activePlayer = getActivePlayerByGameObject(game)
      const interest = Math.floor(activePlayer.money * 0.2)
      return {
        game: {
          ...game,
          situation: {
            ...game.situation,

            players: game.situation.players.map((player) =>
              player.order === activePlayer.order
                ? {
                    ...player,
                    money: player.money + interest,
                  }
                : player
            ),
          },
        },
        customEvents: [],
        postCardDrawEvents: [
          {
            type: "money-transfer",
            from: bankOrder,
            to: activePlayer.order,
            money: interest,
            reason: "action-card",
          },
        ],
      }
    },
    image: "",
  },
  {
    id: "forced-auction",
    text: "Force an auction on any unsold property",
    changeFunction: (game, _, __) => {
      const activePlayer = getActivePlayerByGameObject(game)
      const interest = activePlayer.money * 0.2
      return {
        game: {
          ...game,
          situation: {
            ...game.situation,

            players: game.situation.players.map((player) =>
              player.order === activePlayer.order
                ? {
                    ...player,
                    money: player.money + interest,
                  }
                : player
            ),
          },
        },
        customEvents: [],
        postCardDrawEvents: [
          {
            type: "money-transfer",
            from: bankOrder,
            to: activePlayer.order,
            money: interest,
            reason: "action-card",
          },
        ],
      }
    },
    image: "",
  },

  {
    id: "forced-buy",
    text: "Buy the property on which you landed without auction",
    image: "",
    changeFunction: (game, _, __) => {
      return { game: game, customEvents: [], postCardDrawEvents: [] }
    },
  },
  // {
  //   id: "sell-back-property-at-high-price",
  //   text: "Sell back any property to bank at double price",
  //   image: "",
  //   changeFunction: (game, _, __, { targetedPropertyOrder }) => {
  //     console.log(targetedPropertyOrder, "Property order forced to downgrade")
  //     const property = getCellByMapIdAndOrder(game.settings.mapId, targetedPropertyOrder);
  //     const targetedPlayer = game.situation.players.find(
  //       (item) => item.id === game.situation.turn
  //     )
  //     return {
  //       game: {
  //         ...game,
  //         situation: {
  //           ...game.situation,
  //           players: game.situation.players.map((player) =>
  //             player.order === targetedPropertyOrder
  //               ? { ...player, money: player.money + player.properties.length * 100 }
  //               : player
  //           ),
  //         },
  //       },
  //       customEvents: [],
  //       postCardDrawEvents: [],
  //     }
  //   },
  // },
  {
    id: "force-downgrade-property",
    text: "Remove house of any player",
    image: "",
    changeFunction: (game, _, __, { targetedPropertyOrder }) => {
      console.log(targetedPropertyOrder, "Property order forced to downgrade")
      const currentHouses =
        game.situation.housesData.find(
          (h) => h.propertyOrder === targetedPropertyOrder
        )?.houses || 0
      // Animate the top house being removed, exactly like selling a house. The
      // index convention matches getUpdatedGameByPropertyManagmentChanges.
      const houseAnimations: IHouseAnimation[] =
        currentHouses > 0
          ? [
              {
                type: "remove",
                propertyOrder: targetedPropertyOrder,
                index: currentHouses - 1,
                delay: 0,
              },
            ]
          : []
      return {
        game: {
          ...game,
          situation: {
            ...game.situation,
            housesData: game.situation.housesData.map((houseData) =>
              houseData.propertyOrder === targetedPropertyOrder
                ? { ...houseData, houses: houseData.houses - 1 }
                : houseData
            ),
          },
        },
        customEvents: [],
        postCardDrawEvents: [
          // Danger/loss highlight on the targeted property so it's clear which
          // property this card hit.
          {
            type: "force-downgrade-highlight",
            propertyOrder: targetedPropertyOrder,
          },
          ...(houseAnimations.length > 0
            ? [
                {
                  type: "property-changes-applied" as const,
                  data: { houseAnimations, mortgageAnimations: [] },
                  totalDuration:
                    calculateTotalDurationOfHouseChangeAnimation(
                      houseAnimations,
                      game
                    ),
                  netMoneyChange: 0,
                },
              ]
            : []),
        ],
      }
    },
  },
  {
    id: "safe-round",
    text: "Get saved from all the rents until you pass start again",
    image: "",
    changeFunction: (game, _, __) => {
      return {
        game: {
          ...game,
          situation: {
            ...game.situation,
            players: game.situation.players.map((player) =>
              player.id === game.situation.turn
                ? { ...player, savedFromRent: true }
                : player
            ),
          },
        },
        customEvents: [],
        postCardDrawEvents: [],
      }
    },
  },
  {
    id: "purchase-any-property-for-double-price",
    text: "Purchase any unsold property at double price",
    image: "",
    changeFunction: (game, _, __, { targetedPropertyOrder }) => {
      return { game: game, customEvents: [], postCardDrawEvents: [] }
    },
  },
  {
    id: "apply-property-tax",
    text: "Apply property tax on any player",
    image: "",
    changeFunction: (game, _, __, { targetedPlayerOrder }) => {
      // console.log(targetedPropertyOrder, "Property order forced to downgrade")
      const targetedPlayer = game.situation.players.find(
        (item) => item.order === targetedPlayerOrder
      )
      const moneyToTake = getPropertyTaxPerProperty(game.settings.mapId)
      const taxToPay = targetedPlayer.properties.length * moneyToTake
      return {
        game: {
          ...game,
          situation: {
            ...game.situation,
            moneyOnTable: game.situation.moneyOnTable + taxToPay,
            players: game.situation.players.map((player) =>
              player.order === targetedPlayerOrder
                ? { ...player, money: player.money - taxToPay }
                : player
            ),
          },
        },
        customEvents: [],
        postCardDrawEvents: [
          {
            type: "money-transfer",
            from: targetedPlayerOrder,
            to: bankOrder,
            money: taxToPay,
            reason: "action-card",
          },
        ],
      }
    },
  },
]
// console.log(JSON.
//   allChances.map((item) => ({ id: item.id, text: item.text })))
